import { useEffect, useMemo, useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { api } from '../lib/spizirnaUi';
import { delkaSms } from './SmsPrehled';

/**
 * Správa → Integrace → SMS → Šablony: automatické SMS zákazníkům.
 *
 * Každá šablona má režim (Vypnuto / Jen ukládat / Odesílat) a text se
 * značkami. „Jen ukládat" = zkušební provoz nové SMS: zapíše se do
 * přehledu odeslaných, ale neodejde. Počítadlo počítá s ukázkovými
 * hodnotami značek (číslo 128, krátký odkaz) stejně jako server, který
 * text delší než jedna SMS neuloží. Dole údaje pro stránku objednávky
 * (telefon bistra, odkaz na recenzi Google). Návrh: plátno „Sledování
 * objednávky — design" (2026-10-14).
 */
const REZIMY = [
    { id: 'vypnuto', label: 'Vypnuto', barva: null },
    { id: 'ulozit', label: 'Jen ukládat', barva: '#F59E0B' },
    { id: 'odesilat', label: 'Odesílat', barva: '#10b981' },
];
const SECTION = '#6C4192';

const vyplnit = (text, ukazka) => text.replace(/\{([a-z_]+)\}/g, (_, z) => ukazka[z] ?? '').replace(/[ \t]+/g, ' ').trim();
const bezDiakritiky = (t) => t.normalize('NFD').replace(/[̀-ͯ]/g, '');

export default function SmsSablony({ isDark }) {
    const [data, setData] = useState(null);
    const [sablony, setSablony] = useState([]);
    const [udaje, setUdaje] = useState({ telefon_bistra: '', recenze_odkaz: '' });
    const [vybrana, setVybrana] = useState(0);
    const [zmeneno, setZmeneno] = useState(false);
    const [uklada, setUklada] = useState(false);
    const [chyba, setChyba] = useState('');
    const [ulozeno, setUlozeno] = useState(false);

    const prijmi = (d) => {
        setData(d);
        setSablony(d.sablony.map((s) => ({ id: s.id, text: s.text, rezim: s.rezim })));
        setUdaje({ telefon_bistra: d.telefon_bistra || '', recenze_odkaz: d.recenze_odkaz || '' });
        setZmeneno(false);
    };
    useEffect(() => { api('/api/sms/sablony').then(prijmi).catch(() => setChyba('Šablony se nepodařilo načíst.')); }, []);

    const muted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.04)' : '#fff';
    const plocha = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';
    const akcentText = isDark ? '#C4A3E3' : SECTION;

    const rozbor = useMemo(() => {
        if (!data) return [];
        return sablony.map((s, i) => {
            const def = data.sablony[i];
            const plny = vyplnit(s.text, data.ukazka);
            const delka = delkaSms(plny);
            const chybi = def.povinne.filter((z) => !s.text.includes(`{${z}}`));
            const nezname = [...s.text.matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1]).filter((z) => !def.znacky.includes(z));
            return { def, plny, delka, chybi, nezname, diakritika: /[^\x00-\x7F]/.test(s.text), ok: delka.casti === 1 && !chybi.length && !nezname.length && s.text.trim() };
        });
    }, [data, sablony]);

    const zmen = (i, zmena) => {
        setSablony((p) => p.map((s, j) => (j === i ? { ...s, ...zmena } : s)));
        setVybrana(i);
        setZmeneno(true);
        setUlozeno(false);
    };

    const ulozit = async () => {
        setChyba('');
        setUklada(true);
        try {
            prijmi(await api('/api/sms/sablony', 'PUT', { sablony, ...udaje }));
            setUlozeno(true);
        } catch (e) {
            const zpravy = e?.errors ? Object.values(e.errors).flat().join(' ') : null;
            setChyba(zpravy || e?.message || 'Uložit se nepodařilo.');
        } finally {
            setUklada(false);
        }
    };

    if (!data) {
        return chyba
            ? <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>
            : <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;
    }

    const vsechnoOk = rozbor.every((r) => r.ok);
    const nahled = rozbor[vybrana];
    const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

    return (
        <div className="max-w-5xl" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-xs" style={{ color: muted, margin: 0, maxWidth: 560 }}>
                    SMS, které OS posílá zákazníkům sám. Bez diakritiky a do 160 znaků, ať je to jedna SMS.
                    „Jen ukládat“ zprávu zapíše do odeslaných, ale neodešle — na zkoušku po nasazení.
                </p>
                <div className="flex items-center gap-3">
                    {zmeneno && <span className="text-xs font-semibold" style={{ color: '#F59E0B' }}>Neuložené změny</span>}
                    {ulozeno && !zmeneno && <span className="text-xs font-semibold flex items-center gap-1" style={{ color: '#10b981' }}><Check size={13} /> Uloženo</span>}
                    <button type="button" onClick={ulozit} disabled={!zmeneno || !vsechnoOk || uklada}
                        className="text-xs font-semibold rounded-lg flex items-center gap-1.5"
                        style={{ height: 34, padding: '0 14px', border: 'none', cursor: zmeneno && vsechnoOk ? 'pointer' : 'default',
                            background: zmeneno && vsechnoOk ? SECTION : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                            color: zmeneno && vsechnoOk ? '#fff' : muted }}>
                        {uklada && <Loader2 size={13} className="animate-spin" />} Uložit změny
                    </button>
                </div>
            </div>
            {chyba && <p className="text-xs" style={{ color: '#EF4444', margin: 0 }}>{chyba}</p>}

            <div className="flex flex-wrap items-start" style={{ gap: 14 }}>
                <div style={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {sablony.map((s, i) => {
                        const r = rozbor[i];
                        const aktivni = i === vybrana;
                        return (
                            <section key={s.id} className="rounded-xl" style={{ padding: 16, background: plocha, border: `1px solid ${aktivni ? SECTION : border}`, boxShadow: aktivni ? `0 0 0 1px ${SECTION}` : 'none' }}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <button type="button" onClick={() => setVybrana(i)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', color: 'inherit' }}>
                                        <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{r.def.nazev}</div>
                                        <div className="text-xs mt-0.5" style={{ color: muted }}>Kdy: {r.def.kdy}</div>
                                    </button>
                                    <div role="group" aria-label="Režim šablony" className="flex rounded-lg" style={{ gap: 2, padding: 3, border: `1px solid ${border}`, background: fieldBg }}>
                                        {REZIMY.map((m) => {
                                            const on = s.rezim === m.id;
                                            return (
                                                <button key={m.id} type="button" aria-pressed={on} onClick={() => zmen(i, { rezim: m.id })}
                                                    className="text-xs rounded-md"
                                                    style={{ height: 28, padding: '0 10px', border: 'none', cursor: 'pointer', fontWeight: on ? 700 : 600,
                                                        background: on ? (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)') : 'transparent',
                                                        color: on ? (m.barva || 'var(--text-primary)') : muted }}>
                                                    {m.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <label htmlFor={`sablona-${s.id}`} className="block text-xs font-semibold" style={{ color: muted, margin: '12px 0 6px' }}>Text SMS</label>
                                <textarea id={`sablona-${s.id}`} value={s.text} rows={3} onFocus={() => setVybrana(i)}
                                    onChange={(e) => zmen(i, { text: e.target.value })}
                                    className="w-full rounded-lg text-sm"
                                    style={{ ...mono, fontSize: 13, lineHeight: 1.55, padding: '10px 12px', background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', resize: 'vertical' }} />

                                <div className="flex flex-wrap items-center justify-between gap-2" style={{ marginTop: 8 }}>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="text-xs" style={{ color: muted }}>Vložit:</span>
                                        {r.def.znacky.map((z) => (
                                            <button key={z} type="button" onClick={() => zmen(i, { text: `${s.text}${s.text.endsWith(' ') ? '' : ' '}{${z}}` })}
                                                className="rounded-md text-xs"
                                                style={{ ...mono, height: 26, padding: '0 8px', cursor: 'pointer', border: `1px solid ${isDark ? '#3A2C48' : '#DCD3E6'}`, background: isDark ? '#1E1726' : '#F7F2FB', color: akcentText }}>
                                                {`{${z}}`}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="text-xs font-bold" style={{ fontVariantNumeric: 'tabular-nums', color: r.delka.casti === 1 ? '#10b981' : '#EF4444' }}>
                                        {r.delka.znaku} / {r.delka.kodovani === 'gsm' ? 160 : 70} znaků · {r.delka.casti} SMS
                                    </span>
                                </div>

                                {r.diakritika && (
                                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg text-xs" style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(245,158,11,0.10)', color: isDark ? '#FFCB8A' : '#92400E' }}>
                                        <span>Text má diakritiku. S háčky a čárkami se do jedné SMS vejde jen 70 znaků.</span>
                                        <button type="button" onClick={() => zmen(i, { text: bezDiakritiky(s.text) })}
                                            className="font-semibold rounded-md" style={{ height: 28, padding: '0 10px', border: 'none', background: '#FF8A00', color: '#17181A', cursor: 'pointer' }}>
                                            Odstranit diakritiku
                                        </button>
                                    </div>
                                )}
                                {r.chybi.length > 0 && (
                                    <div className="rounded-lg text-xs" style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(239,68,68,0.10)', color: '#EF4444' }}>
                                        Chybí {r.chybi.map((z) => `{${z}}`).join(', ')}. Bez odkazu se zákazník ke stránce objednávky nedostane.
                                    </div>
                                )}
                                {r.nezname.length > 0 && (
                                    <div className="rounded-lg text-xs" style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(239,68,68,0.10)', color: '#EF4444' }}>
                                        Značku {r.nezname.map((z) => `{${z}}`).join(', ')} tahle SMS nezná.
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>

                <aside style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <section className="rounded-xl" style={{ padding: 16, background: plocha, border: `1px solid ${border}` }}>
                        <h4 className="text-xs font-bold uppercase" style={{ color: muted, letterSpacing: '0.06em', margin: 0 }}>Náhled</h4>
                        <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)', marginTop: 4 }}>{nahled?.def.nazev}</div>
                        <div style={{ marginTop: 12, background: '#F0EEEB', color: '#17181A', borderRadius: '16px 16px 16px 6px', padding: '10px 13px', fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {nahled?.plny}
                        </div>
                        <div className="text-xs" style={{ color: muted, marginTop: 6 }}>
                            Ukázka: číslo {data.ukazka.cislo}, odkaz {data.ukazka.odkaz}
                        </div>
                    </section>

                    <section className="rounded-xl" style={{ padding: 16, background: plocha, border: `1px solid ${border}` }}>
                        <h4 className="text-xs font-bold uppercase" style={{ color: muted, letterSpacing: '0.06em', margin: 0 }}>Stránka objednávky</h4>
                        <label htmlFor="sms-telefon-bistra" className="block text-xs font-semibold" style={{ color: muted, margin: '10px 0 4px' }}>Telefon bistra pro zákazníky</label>
                        <input id="sms-telefon-bistra" value={udaje.telefon_bistra} placeholder="+420 …"
                            onChange={(e) => { setUdaje((u) => ({ ...u, telefon_bistra: e.target.value })); setZmeneno(true); setUlozeno(false); }}
                            className="w-full rounded-lg text-sm" style={{ height: 36, padding: '0 10px', background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                        <label htmlFor="sms-recenze" className="block text-xs font-semibold" style={{ color: muted, margin: '10px 0 4px' }}>Odkaz na recenzi Google</label>
                        <input id="sms-recenze" value={udaje.recenze_odkaz} placeholder="https://g.page/r/…/review"
                            onChange={(e) => { setUdaje((u) => ({ ...u, recenze_odkaz: e.target.value })); setZmeneno(true); setUlozeno(false); }}
                            className="w-full rounded-lg text-sm" style={{ height: 36, padding: '0 10px', background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                        <p className="text-xs" style={{ color: muted, margin: '6px 0 0' }}>
                            Najdete ho v Google profilu firmy → Požádat o recenze. Bez něj stránka tlačítko na recenzi neukáže.
                        </p>
                    </section>

                    <section className="rounded-xl" style={{ padding: 16, background: plocha, border: `1px solid ${border}` }}>
                        <h4 className="text-xs font-bold uppercase" style={{ color: muted, letterSpacing: '0.06em', margin: 0 }}>Kdy se SMS nepošle</h4>
                        <ul className="text-xs" style={{ color: muted, margin: '8px 0 0', paddingLeft: 16, lineHeight: 1.7 }}>
                            <li>objednávka bez telefonu</li>
                            <li>stornovaná objednávka</li>
                            <li>objednávka u stolu v bistru</li>
                            <li>telefonická objednávka (potvrzení posílá průvodce hovorem)</li>
                            <li>stejná objednávka podruhé, i po nové synchronizaci</li>
                            <li>celé SMS v testovacím režimu brány (jen se uloží)</li>
                        </ul>
                    </section>
                </aside>
            </div>
        </div>
    );
}
